
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, Users, Lightbulb, Target, TrendingUp, Search } from 'lucide-react';
import type { Contact } from '@/pages/PersonalizeOutreach';

interface ResearchConfigProps {
  contacts: Contact[];
  onResearchStarted: (salesPitch: string, additionalContext?: string) => void;
  onSkip: () => void;
}

const ResearchConfig = ({ contacts, onResearchStarted, onSkip }: ResearchConfigProps) => {
  const [salesPitch, setSalesPitch] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  const handleStartResearch = () => {
    if (!salesPitch.trim()) {
      return;
    }
    onResearchStarted(salesPitch, additionalContext);
  };

  const examplePitches = [
    "We provide AI-powered sales automation tools that help sales teams increase their conversion rates by 40% through personalized outreach and intelligent lead scoring. Our platform integrates with existing CRM systems and provides real-time analytics to optimize sales performance.",
    "Our cloud-based project management platform streamlines team collaboration and reduces project delivery times by up to 30% for growing businesses. We offer advanced resource allocation, automated reporting, and seamless integration with popular development tools.",
    "We offer comprehensive cybersecurity solutions that protect small to medium businesses from data breaches and ensure compliance with industry regulations. Our managed security services include 24/7 monitoring, threat detection, and incident response."
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Additional Context (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-900">
              This stage is optional. The AI will use the Knowledge Base prompt for research and personalization. 
              You can provide additional context here to enhance the personalization further.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {contacts.length} contacts selected
            </Badge>
            <Badge variant="outline" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              AI Research with Web Search
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Deep Research</span>
              </div>
              <p className="text-sm text-blue-800">Comprehensive company analysis, recent news, and industry insights</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Value-First</span>
              </div>
              <p className="text-sm text-green-800">Messages lead with insights and value, not product pitches</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-purple-900">Hyper-Targeted</span>
              </div>
              <p className="text-sm text-purple-800">Specific insights tailored to each prospect's situation</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="sales-pitch" className="text-base font-medium">
                Your Solution (Optional but Recommended)
              </Label>
              <p className="text-sm text-gray-600 mt-1 mb-3">
                Provide details about your product or service to help the AI connect your solution to prospect needs.
              </p>
              <Textarea
                id="sales-pitch"
                placeholder="Example: We provide AI-powered sales automation tools that help sales teams increase their conversion rates by 40%..."
                value={salesPitch}
                onChange={(e) => setSalesPitch(e.target.value)}
                className="min-h-24"
              />
            </div>

            <div>
              <Label htmlFor="additional-context" className="text-base font-medium">
                Additional Context (Optional)
              </Label>
              <p className="text-sm text-gray-600 mt-1 mb-3">
                Add any specific context, recent campaigns, industry insights, or talking points you want the AI to consider.
              </p>
              <Textarea
                id="additional-context"
                placeholder="Example: We're focusing on companies that recently raised Series A funding. Emphasize our ROI metrics from similar-stage startups..."
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                className="min-h-24"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium">Enhanced example descriptions:</span>
              </div>
              <div className="space-y-2">
                {examplePitches.map((example, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setSalesPitch(example)}
                  >
                    <p className="text-sm text-gray-700">{example}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">AI Research Capabilities</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• <strong>Real-Time Web Search:</strong> Find recent company news, funding, and initiatives</li>
              <li>• <strong>LinkedIn Analysis:</strong> Review professional background and activity</li>
              <li>• <strong>Industry Intelligence:</strong> Market trends and competitive landscape</li>
              <li>• <strong>Value-First Messages:</strong> Lead with insights, not pitches</li>
              <li>• <strong>Deep Personalization:</strong> Reference specific, verified details</li>
            </ul>
          </div>

          <div className="flex justify-between pt-4">
            <Button 
              onClick={onSkip}
              variant="outline"
              size="lg"
            >
              Skip & Use Knowledge Base Only
            </Button>
            <Button 
              onClick={handleStartResearch}
              disabled={!salesPitch.trim()}
              size="lg"
              className="px-8"
            >
              Start AI Research
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResearchConfig;
